import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function LoginPage() {
  const { login, verifyTotp, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Secondo passo, solo quando l'account ha il 2FA attivo (vedi AuthContext.login): il
  // pendingToken identifica la sessione di login in corso, non ancora una sessione completa.
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);

  if (isAuthenticated) {
    navigate('/', { replace: true });
    return null;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await login(email, password);
      if (result.requires2fa) {
        setPendingToken(result.pendingToken);
      } else {
        navigate('/', { replace: true });
      }
    } catch (err: any) {
      // Mostra il motivo vero restituito dal server invece di attribuire sempre l'errore alle
      // credenziali: il backend distingue casi diversi (rate limit dopo 5 tentativi, account
      // disattivato, errore interno) e dirli "email o password errati" manda l'utente a ritentare
      // proprio quando ritentare peggiora le cose. Stesso trattamento già usato nel passo 2FA.
      setError(err.response?.data?.error || 'Invalid email or password');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await verifyTotp(pendingToken!, code);
      navigate('/', { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid code');
    } finally {
      setSubmitting(false);
    }
  }

  if (pendingToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <form onSubmit={handleVerify} className="card p-8 w-full max-w-sm">
          <div className="flex items-center justify-center mb-4 text-primary-600">
            <ShieldCheck size={36} />
          </div>
          <h1 className="text-center text-base font-medium text-gray-900 mb-1">Two-factor verification</h1>
          <p className="text-center text-xs text-gray-500 mb-6">
            {useBackupCode ? 'Enter one of your backup codes.' : 'Enter the 6-digit code from your authenticator app.'}
          </p>
          {error && <div className="mb-4 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>}
          <input
            type="text"
            inputMode={useBackupCode ? 'text' : 'numeric'}
            autoFocus
            value={code}
            onChange={e => setCode(e.target.value)}
            placeholder={useBackupCode ? 'XXXXX-XXXXX' : '000000'}
            required
            className="input-field mb-4 text-center tracking-widest font-mono"
          />
          <button type="submit" disabled={submitting} className="btn-primary w-full mb-3">
            {submitting ? 'Verifying…' : 'Verify'}
          </button>
          <button
            type="button"
            onClick={() => { setUseBackupCode(v => !v); setCode(''); setError(null); }}
            className="w-full text-xs text-gray-500 hover:text-gray-700"
          >
            {useBackupCode ? 'Use my authenticator app instead' : "I don't have my device — use a backup code"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <form onSubmit={handleSubmit} className="card p-8 w-full max-w-sm">
        <div className="flex items-center justify-center mb-6">
          <img src="/logo_omega_trasp.png" alt="OmegaLab Logo" className="h-16 w-auto object-contain" />
        </div>
        {error && <div className="mb-4 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>}
        <label className="block text-sm text-gray-600 mb-1">Email</label>
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          required
          className="input-field mb-4"
        />
        <label className="block text-sm text-gray-600 mb-1">Password</label>
        <input
          type="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          required
          className="input-field mb-6"
        />
        <button type="submit" disabled={submitting} className="btn-primary w-full">
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}
